import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { AxiosResponse, isAxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { APIError } from '@api/types';
import { get, post } from '@api/httpClient';
import queryClient from '@api/queryClient';

type UseRunExperimentParams = {
  id: string;
};

type Status = 'pending' | 'completed' | 'in_progress' | 'failed';

// POST /flowgate/run_job/ now submits to the pipeline service inline and
// returns the real outcome. A 200 means the run was accepted: status is
// 'in_progress' (previously the fire-and-forget 'waiting') and the body
// carries the pipeline-service batch job id. Completion still happens
// asynchronously, so callers keep polling the experiment status endpoint.
type UseRunExperimentResponse = {
  id: string;
  batch_job_id: string;
  status: Extract<Status, 'in_progress'>;
};

// run_job error bodies:
//   422 — pipeline service rejected the inputs: { detail, errors: [...] }
//   400 — experiment misconfigured (e.g. no pipeline selected): { detail }
//   503 — pipeline service unreachable (nothing submitted): { detail }
type RunJobErrorBody = {
  detail?: string;
  errors?: string[];
};

// Map a failed run_job response to the user-facing message(s) to surface.
const getRunJobErrorMessages = (error: unknown): string[] => {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const data = (error.response?.data ?? {}) as RunJobErrorBody;

    // 422 — actionable input problems; show each one to the user.
    if (status === 422 && data.errors?.length) {
      return data.errors;
    }

    // 503 — pipeline service unreachable; the run was not submitted.
    if (status === 503) {
      return ['Pipeline service unavailable, please retry.'];
    }

    // 400 — experiment misconfigured; surface the server-provided detail.
    if (data.detail) {
      return [data.detail];
    }
  }

  return ['Failed to run experiment.'];
};

type UseExperimentStatusResponse = {
  status: Status;
};

// GET /experiments/<id>/ — shape derived from ExperimentSerializer (Meta.fields
// = "__all__" on the Experiment model). Only the fields used by the UI are
// pinned here; everything else stays loose so unrelated schema additions on
// the BE don't break the FE.
export type ExperimentDetail = {
  id: string;
  name?: string;
  description?: string | null;
  experiment_type?: 'single' | 'trio' | 'custom';
  status?: string;
  samples?: string[];
  flowgate_pipeline_id?: string | null;
  pipeline_config?: string | null;
  batch_job_id?: string | null;
  experiment_group?: string | null;
  project?: string | null;
  created_at?: string;
  updated_at?: string;
};

const QUERY_KEYS = {
  EXPERIMENT_STATUS: (experimentId: string) => [
    'experiments',
    'status',
    { id: experimentId },
  ],
  EXPERIMENT: (experimentId: string) => ['experiments', { id: experimentId }],
};

export const useRunExperiment = () =>
  useMutation<
    AxiosResponse<UseRunExperimentResponse>,
    APIError,
    UseRunExperimentParams
  >({
    mutationFn: ({ id }) =>
      post<UseRunExperimentResponse>('flowgate/run_job/', {
        experiment_id: id,
      }),
    // 200 — run was submitted (status 'in_progress'). Invalidate the experiment
    // status query so polling picks up the async move to completed/failed.
    onSuccess: ({ data: { id } }) =>
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EXPERIMENT_STATUS(id),
      }),
    // 422/400/503 — surface specific, actionable messages to the user.
    onError: (error) =>
      getRunJobErrorMessages(error).forEach((message) =>
        enqueueSnackbar(message, { variant: 'error' }),
      ),
  });

export const useGetExperiment = (id?: string) =>
  useSuspenseQuery<ExperimentDetail | null>({
    queryKey: QUERY_KEYS.EXPERIMENT(id || ''),
    queryFn: async () => {
      if (!id) return null;

      const resp = await get<ExperimentDetail>(`experiments/${id}/`);
      return resp.data;
    },
  });

export const useExperimentStatus = ({ id }: { id?: string }) =>
  useSuspenseQuery<UseExperimentStatusResponse | null>({
    queryKey: QUERY_KEYS.EXPERIMENT_STATUS(id || ''),
    queryFn: async () => {
      if (!id) return null;

      const resp = await get<UseExperimentStatusResponse>(
        `experiments/${id}/status`,
      );
      return resp.data;
    },
  });
